import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, AlertCircle, UserPlus } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { MagneticButton } from "../components/MagneticButton";
import { GoogleGIcon } from "../components/GoogleGIcon";
import { PasswordStrengthMeter } from "../components/PasswordStrengthMeter";
import { useAuthStore } from "../state/authStore";
import { SEO } from "../components/SEO";

function validate({ name, email, password, confirm }) {
    if (name.trim().length < 2) return "Name must be at least 2 characters.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password))
        return "Password must include a letter and a number.";
    if (password !== confirm) return "Passwords don't match.";
    return null;
}

export const Signup = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next") || "/dashboard";
    const { signup } = useAuthStore();

    const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
    const [showPw, setShowPw] = useState(false);
    const [status, setStatus] = useState("idle");
    const [err, setErr] = useState("");

    const startGoogle = () => {
        // window.location.origin so this works on preview, production, and
        // custom domains without rebuilding.
        const back = window.location.origin;
        window.location.href = `${back}/api/auth/google/login?next=/dashboard`;
    };

    const submit = async (e) => {
        e.preventDefault();
        if (status === "loading") return;
        const v = validate(form);
        if (v) {
            setErr(v);
            return;
        }
        setStatus("loading");
        setErr("");
        try {
            await signup(form.name.trim(), form.email.trim(), form.password);
            navigate(decodeURIComponent(next));
        } catch (e2) {
            setStatus("idle");
            setErr(e2.message || "Could not create your account.");
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-12 mx-auto max-w-6xl px-5 md:px-10" data-testid="signup-page">
            <SEO noindex title="Create account" canonicalPath="/signup" />
            <div className="grid lg:grid-cols-12 gap-10 items-center">
                <Reveal className="lg:col-span-5">
                    <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                        Create account
                    </p>
                    <h1
                        className="mt-4 font-display font-semibold text-4xl sm:text-5xl tracking-tight leading-[1.05]"
                        data-testid="signup-title"
                    >
                        Join the <span className="text-accent-cyan">Forefront</span>.
                    </h1>
                    <p className="mt-5 text-text-muted leading-relaxed max-w-md">
                        Start free, upgrade when you&apos;re ready to ship ZeroConf in
                        production.
                    </p>
                </Reveal>

                <Reveal className="lg:col-span-7" delay={0.1}>
                    <div className="rounded-2xl border border-white/[0.06] bg-surface/70 backdrop-blur-md p-6 md:p-8 max-w-md ml-auto">
                        <button
                            type="button"
                            onClick={startGoogle}
                            className="w-full inline-flex items-center justify-center gap-2.5 py-2.5 rounded-md border border-accent-cyan/40 bg-accent-cyan/[0.06] text-[14px] font-medium text-text-primary hover:bg-accent-cyan/[0.12] hover:shadow-glow-cyan transition-all duration-200"
                            data-testid="signup-google-btn"
                        >
                            <GoogleGIcon className="w-4 h-4" />
                            Continue with Google
                        </button>
                        <div className="flex items-center gap-3 my-5">
                            <span className="flex-1 h-px bg-white/[0.06]" />
                            <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                or
                            </span>
                            <span className="flex-1 h-px bg-white/[0.06]" />
                        </div>

                        <form onSubmit={submit} className="space-y-3" data-testid="signup-form">
                            <input
                                type="text"
                                required
                                placeholder="Full name"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="signup-name"
                                autoComplete="name"
                            />
                            <input
                                type="email"
                                required
                                placeholder="Work email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="signup-email"
                                autoComplete="email"
                            />
                            <div className="relative">
                                <input
                                    type={showPw ? "text" : "password"}
                                    required
                                    placeholder="Password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 pr-9 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                    data-testid="signup-password"
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-dim hover:text-text-muted transition-colors duration-150"
                                    aria-label={showPw ? "Hide password" : "Show password"}
                                    tabIndex={-1}
                                >
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                            <PasswordStrengthMeter password={form.password} />
                            <input
                                type={showPw ? "text" : "password"}
                                required
                                placeholder="Confirm password"
                                value={form.confirm}
                                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                className="w-full bg-canvas/60 border border-white/[0.07] rounded-md px-3 py-2.5 text-[13.5px] text-text-primary placeholder:text-text-dim outline-none focus:border-accent-cyan/50 transition-colors duration-200"
                                data-testid="signup-confirm"
                                autoComplete="new-password"
                            />
                            {err && (
                                <div
                                    className="flex items-center gap-2 text-[12px] text-accent-red bg-accent-red/5 border border-accent-red/20 rounded-md px-3 py-2"
                                    data-testid="signup-error"
                                >
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                                    {err}
                                </div>
                            )}
                            <MagneticButton
                                type="submit"
                                disabled={status === "loading"}
                                className="w-full py-2.5 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-40 transition-shadow duration-200"
                                data-testid="signup-submit"
                                ariaLabel="Create account"
                            >
                                <span className="inline-flex items-center justify-center gap-2">
                                    <UserPlus className="w-3.5 h-3.5" />
                                    {status === "loading" ? "Creating…" : "Create account"}
                                </span>
                            </MagneticButton>
                        </form>

                        <p className="mt-5 text-center text-[12.5px] text-text-muted">
                            Already have an account?{" "}
                            <Link
                                to="/login"
                                className="text-accent-cyan hover:underline"
                                data-testid="signup-login-link"
                            >
                                Sign in →
                            </Link>
                        </p>
                    </div>
                </Reveal>
            </div>
        </div>
    );
};

export default Signup;
